import { useFragment } from "@apollo/client";
import { memo, useEffect, useMemo, useState } from "react";
import useWindowFocus from "use-window-focus";
import { useIdleTimer } from "react-idle-timer";
import useAppState from "modules/hooks/use-app-state";
import useToggleMessageThreadSeen from "modules/hooks/use-toggle-message-thread-seen";
import useQueryString from "modules/hooks/use-query-string";
import MESSAGE_THREAD_FRAGMENT from "modules/messaging/_networking/message-thread-fragment";

const MESSAGE_THREAD_SEEN_TIMEOUT_MILLISECONDS = 250;
const USER_IDLE_TIMEOUT_MILLISECONDS = 30_000;

export const MessageThreadSeenListener = () => {
  const isWindowFocused = useWindowFocus();
  const [isTimerEnabled, setIsTimerEnabled] = useState(false);
  const [isIdle, setIsIdle] = useState(false);

  useIdleTimer({
    timeout: USER_IDLE_TIMEOUT_MILLISECONDS,
    onIdle: () => setIsIdle(true),
    onActive: () => setIsIdle(false),
  });
  const { qsMessageThreadId } = useQueryString();

  const messageThreadId = useMemo(
    () => (qsMessageThreadId ? [qsMessageThreadId] : []),
    [qsMessageThreadId],
  );

  const { data, complete } = useFragment({
    fragment: MESSAGE_THREAD_FRAGMENT,
    fragmentName: "MessageThread",
    from:
      messageThreadId.length === 1
        ? { __typename: "MessageThread", id: messageThreadId[0] }
        : null,
  });

  const messageThread = complete ? data : null;

  const { deleteValue, stringValue: _messageThreadId } = useAppState({
    appStateType: "messageThreadIdHasSetNotSeen",
  });

  const { toggleSeen } = useToggleMessageThreadSeen({ messageThread });

  useEffect(() => {
    //reset this when the thread changes to the thread doesn't get 'stuck' in an unread state
    if (!messageThread || !_messageThreadId) return;
    if (_messageThreadId !== messageThread.id) {
      deleteValue();
    }
  }, [deleteValue, messageThread, _messageThreadId]);

  useEffect(() => {
    setIsTimerEnabled(true);
  }, [messageThread?.id]);

  useEffect(() => {
    if (!messageThread) return;
    if (
      !isWindowFocused ||
      isIdle ||
      messageThread.isSeen ||
      !isTimerEnabled ||
      _messageThreadId === messageThread.id
    ) {
      return;
    }

    const timeout = setTimeout(() => {
      toggleSeen(true);
    }, MESSAGE_THREAD_SEEN_TIMEOUT_MILLISECONDS);

    return () => {
      clearTimeout(timeout);
    };
  }, [
    isIdle,
    isTimerEnabled,
    isWindowFocused,
    messageThread,
    _messageThreadId,
    toggleSeen,
  ]);

  return null;
};

export default memo(MessageThreadSeenListener);
